import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type * as CSS from 'csstype';
import React, { PropsWithChildren } from 'react';
import { borderRadius } from '../../foundations/borders/borderRadius';
import { color } from '../../foundations/modes/colors/colors';
import { spacing } from '../../foundations/spacing/spacing';
import { SpaceBetween } from '../../utils/SpaceBetween/SpaceBetween';
import { ListHeader } from '../ListHeader/ListHeader';
import { ListItem, ListItemProps } from './ListItem/ListItem';

export interface ListProps {
  items?: ListItemProps[];
  orderedList?: boolean;
  listStyle?: CSS.Property.ListStyleType;
  gap?: spacing;
  border?: boolean;
  limitToParentWidth?: boolean;
}

export function List({
  items,
  orderedList = false,
  listStyle,
  gap = spacing.size0,
  border = false,
  limitToParentWidth = false,
  children,
}: PropsWithChildren<ListProps>) {
  return (
    <StyledList
      listStyle={listStyle}
      direction="y"
      as={orderedList ? 'ol' : 'ul'}
      spacing={gap}
      border={border}
      limitToParentWidth={limitToParentWidth}
    >
      {items?.map((itemProps) =>
        itemProps.header ? (
          <ListHeader {...itemProps} key={itemProps.key || `header-${itemProps.text}`} priority={itemProps.header} />
        ) : (
          <ListItem
            {...itemProps}
            orderedList={orderedList}
            key={itemProps.key || `definition-${itemProps.text}`}
            limitToParentWidth={limitToParentWidth}
          />
        )
      )}
      {children}
    </StyledList>
  );
}

type StyledListProps = Pick<ListProps, 'listStyle' | 'border' | 'limitToParentWidth'>;

const StyledList = styled(SpaceBetween)<StyledListProps>`
  padding-left: 0;
  margin: 0;
  text-align: left;
  ${({ listStyle }) =>
    listStyle &&
    css`
      list-style: ${listStyle};
    `}
  ${({ border }) =>
    border &&
    css`
      border-radius: ${borderRadius.rounded8};
      border: 1px solid ${color.neutral.outline.background};
    `}

  ${({ limitToParentWidth }) =>
    limitToParentWidth &&
    css`
      max-width: 100%;
      overflow: hidden;
    `}
`;
